import type { Board, GeometryElement } from 'jsxgraph';
import { type JxgSlider, asJxgSlider } from '../elements/core/board.types';

/**
 * JSXGraph-native parameter store using hidden sliders.
 *
 * All params are stored as off-screen JSXGraph sliders, making them:
 * - Reactive: JSXGraph's dependency graph auto-updates dependent elements
 * - Observable: Any code can read current values via getValue()
 *
 * Usage:
 * 1. Create store with param definitions
 * 2. Use getValue() in evaluate functions for reactivity
 * 3. Use setParams() to update values (e.g., from UI sliders)
 * 4. Register sync callbacks for handle position updates
 *
 * @example
 * ```typescript
 * const store = new JsxParamStore(board, {
 *   a: { value: 1, range: [-5, 5] },
 *   h: { value: 0, range: [-10, 10] },
 *   k: { value: 0, range: [-10, 10] },
 * });
 *
 * // Reactive evaluation
 * const evaluate = (x: number) => {
 *   const a = store.getValue('a');
 *   const h = store.getValue('h');
 *   const k = store.getValue('k');
 *   return a * Math.pow(x - h, 2) + k;
 * };
 *
 * // Sync handle when params change from UI
 * store.onSync(() => {
 *   handle.setPositionDirectly(1, [store.getValue('h'), store.getValue('k')]);
 * });
 * ```
 */
export interface ParamDefinition {
  /** Initial value */
  value: number;
  /** Min/max range for the hidden slider */
  range?: readonly [number, number];
}

export class JsxParamStore {
  constructor(
    private readonly board: Board,
    params: Record<string, ParamDefinition>,
  ) {
    let index = 0;
    for (const [name, def] of Object.entries(params)) {
      const [min, max] = def.range ?? [def.value - 10, def.value + 10];
      const slider = board.create(
        'slider',
        [
          [0, 1000 + index],
          [1, 1000 + index],
          [min, def.value, max],
        ],
        { visible: false, name: '', withLabel: false },
      );
      this.sliders.set(name, asJxgSlider(slider as GeometryElement));
      index++;
    }
  }

  private readonly sliders = new Map<string, JxgSlider>();
  private readonly syncCallbacks: Array<() => void> = [];
  private readonly displaySteps = new Map<string, number>();

  getValue(name: string): number {
    const slider = this.sliders.get(name);
    if (!slider) {
      throw new Error(`Param '${name}' not found`);
    }
    return slider.Value();
  }

  /**
   * Get the display-rounded value for a parameter.
   * If a display step is configured, snaps to the nearest step.
   * Otherwise returns the raw value (same as getValue).
   */
  getDisplayValue(name: string): number {
    const raw = this.getValue(name);
    const step = this.displaySteps.get(name);
    if (step) {
      return Math.round(raw / step) * step;
    }
    return raw;
  }

  /** Configure the display snap step for a parameter. */
  setDisplayStep(name: string, step: number): void {
    this.displaySteps.set(name, step);
  }

  getParams(): Record<string, number> {
    const result: Record<string, number> = {};
    for (const [name] of this.sliders) {
      result[name] = this.getValue(name);
    }
    return result;
  }

  /** Set a single param value (does not trigger sync or board update) */
  setValue(name: string, value: number): void {
    const slider = this.sliders.get(name);
    if (slider) {
      slider.setValue(value);
    }
  }

  /**
   * Set multiple param values, then run sync callbacks and update board.
   * This is the main entry point for external updates (e.g., UI sliders).
   */
  setParams(params: Record<string, number>): void {
    for (const [name, value] of Object.entries(params)) {
      if (value !== undefined) {
        this.setValue(name, value);
      }
    }
    this.runSyncCallbacks();
    this.board.update();
  }

  /**
   * Register a callback to run after setParams().
   * Use this to sync handle positions or other dependent state.
   *
   * @example
   * ```typescript
   * store.onSync(() => {
   *   const h = store.getValue('h');
   *   const k = store.getValue('k');
   *   handle.setPositionDirectly(1, [h, k]);
   * });
   * ```
   */
  onSync(callback: () => void): void {
    this.syncCallbacks.push(callback);
  }

  /** Get the underlying slider (for direct JSXGraph wiring if needed) */
  getSlider(name: string): GeometryElement | undefined {
    return this.sliders.get(name);
  }

  getAllSliders(): GeometryElement[] {
    return Array.from(this.sliders.values());
  }

  has(name: string): boolean {
    return this.sliders.has(name);
  }

  private runSyncCallbacks(): void {
    for (const callback of this.syncCallbacks) {
      callback();
    }
  }
}

/**
 * Maps handle movement axes to parameter names.
 * Used to configure which params a draggable point controls.
 *
 * For simple cases (like quadratic vertex), the handle IS at (xParam, yParam).
 * For complex cases (like cosine maximum), we need transforms.
 */
export interface HandleParamMapping {
  /** Parameter controlled by horizontal (X) movement */
  xParam: string;
  /** Parameter controlled by vertical (Y) movement */
  yParam: string;

  /**
   * Transform params to visual handle position.
   * Default: (params) => [params[xParam], params[yParam]]
   *
   * @example Cosine handle at first maximum:
   *   toPosition: (p) => [p.c, p.a + p.d]
   */
  toPosition?: (params: Record<string, number>) => [number, number];

  /**
   * Transform handle drag position back to param values.
   * Default: { [xParam]: handleX, [yParam]: handleY }
   *
   * @example Cosine handle:
   *   fromPosition: (x, y, p) => ({ c: x, d: y - p.a })
   */
  fromPosition?: (x: number, y: number, params: Record<string, number>) => Record<string, number>;
}

/**
 * Creates a sync callback that updates a handle's position when params change.
 * Use with `paramStore.onSync()` to keep handles in sync with param values.
 *
 * @param paramStore - The param store to read values from
 * @param handle - The point element to update
 * @param mapping - Maps which params control x and y position
 * @returns A callback function to pass to `paramStore.onSync()`
 */
export function createHandleSync(
  paramStore: JsxParamStore,
  handle: { setPositionDirectly: (type: number, coords: [number, number]) => void },
  mapping: HandleParamMapping,
): () => void {
  return () => {
    const params = paramStore.getParams();
    const [x, y] = mapping.toPosition ? mapping.toPosition(params) : [params[mapping.xParam], params[mapping.yParam]];
    handle.setPositionDirectly(1, [x, y]);
  };
}
